import React, { useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import type {
  FundSubtypeOption,
  FundTypeOption,
} from '../../types/financeFunds';
import {
  fundTypeRequiresCustomName,
  fundSubtypeRequiresNamedLimit,
  NAMED_SCHOLARSHIP_LIMIT_OPTIONS,
} from '../../types/financeFunds';

export interface AssignedTypeRow {
  type_id: string;
  subtype_id: string;
  percentage: string;
  other_transfer_name: string;
  principal_amount: string;
  named_limit: string;
}

interface FundTypeAllocationEditorProps {
  rows: AssignedTypeRow[];
  categoryId: string;
  typeOptions: FundTypeOption[];
  subtypeOptions: FundSubtypeOption[];
  onChange: (rows: AssignedTypeRow[]) => void;
}

const FundTypeAllocationEditor: React.FC<FundTypeAllocationEditorProps> = ({
  rows,
  categoryId,
  typeOptions,
  subtypeOptions,
  onChange,
}) => {
  const filteredTypes = useMemo(
    () => typeOptions.filter((t) => t.category_id === categoryId),
    [typeOptions, categoryId]
  );

  const total = useMemo(
    () =>
      rows.reduce((sum, row) => {
        const n = parseFloat(row.percentage);
        return sum + (Number.isFinite(n) ? n : 0);
      }, 0),
    [rows]
  );

  const totalValid = Math.abs(total - 100) < 0.01;

  const subtypesForType = (typeId: string) =>
    subtypeOptions.filter((s) => s.type_id === typeId);

  const updateRow = (index: number, patch: Partial<AssignedTypeRow>) => {
    const next = rows.map((row, i) =>
      i === index ? { ...row, ...patch } : row
    );
    onChange(next);
  };

  const addRow = () => {
    onChange([
      ...rows,
      {
        type_id: '',
        subtype_id: '',
        percentage: '',
        other_transfer_name: '',
        principal_amount: '',
        named_limit: '',
      },
    ]);
  };

  const removeRow = (index: number) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  return (
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Type allocations
      </Typography>
      {rows.map((row, index) => {
        const rowSubtypes = subtypesForType(row.type_id);
        const subtypeRequired = rowSubtypes.length > 0;
        return (
          <Box
            key={`alloc-row-${index}`}
            sx={{
              display: 'flex',
              gap: 1,
              mb: 1,
              alignItems: 'flex-start',
              flexWrap: 'wrap',
            }}
          >
            <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={row.type_id}
                onChange={(e) => {
                  const type_id = String(e.target.value);
                  updateRow(index, {
                    type_id,
                    subtype_id: '',
                    other_transfer_name: '',
                    principal_amount: '',
                    named_limit: '',
                  });
                }}
              >
                <MenuItem value="">
                  <em>Select type</em>
                </MenuItem>
                {filteredTypes.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {subtypeRequired && (
              <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
                <InputLabel>Subtype</InputLabel>
                <Select
                  label="Subtype"
                  value={row.subtype_id}
                  onChange={(e) => {
                    const subtype_id = String(e.target.value);
                    updateRow(index, {
                      subtype_id,
                      principal_amount: subtype_id ? row.principal_amount : '',
                      named_limit:
                        subtype_id && fundSubtypeRequiresNamedLimit(subtype_id)
                          ? row.named_limit
                          : '',
                    });
                  }}
                >
                  <MenuItem value="">
                    <em>Select subtype</em>
                  </MenuItem>
                  {rowSubtypes.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {subtypeRequired && row.subtype_id && (
              <TextField
                label="Amount"
                size="small"
                type="number"
                value={row.principal_amount}
                onChange={(e) =>
                  updateRow(index, { principal_amount: e.target.value })
                }
                sx={{ minWidth: 120, flex: 1 }}
                inputProps={{ min: 0.01, step: '0.01' }}
              />
            )}
            {fundSubtypeRequiresNamedLimit(row.subtype_id) && (
              <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                <InputLabel>Named scholarship limit</InputLabel>
                <Select
                  label="Named scholarship limit"
                  value={row.named_limit}
                  onChange={(e) =>
                    updateRow(index, { named_limit: String(e.target.value) })
                  }
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {NAMED_SCHOLARSHIP_LIMIT_OPTIONS.map((n) => (
                    <MenuItem key={n} value={String(n)}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {fundTypeRequiresCustomName(row.type_id) && (
              <TextField
                label="Custom name"
                size="small"
                required
                value={row.other_transfer_name}
                onChange={(e) =>
                  updateRow(index, { other_transfer_name: e.target.value })
                }
                sx={{ minWidth: 200, flex: 1 }}
                inputProps={{ maxLength: 255 }}
              />
            )}
            <TextField
              label="%"
              size="small"
              type="number"
              value={row.percentage}
              onChange={(e) => updateRow(index, { percentage: e.target.value })}
              sx={{ width: 90 }}
              inputProps={{ min: 0, max: 100, step: '0.01' }}
            />
            <IconButton
              aria-label="Remove allocation row"
              onClick={() => removeRow(index)}
              disabled={rows.length <= 1}
              size="small"
            >
              <Delete />
            </IconButton>
          </Box>
        );
      })}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
        <Button startIcon={<Add />} size="small" onClick={addRow}>
          Add type
        </Button>
        <Typography
          variant="body2"
          color={totalValid ? 'text.secondary' : 'error'}
        >
          Total: {total.toFixed(2)}%
        </Typography>
      </Box>
    </Grid>
  );
};

export default FundTypeAllocationEditor;
